import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LogOut,
  LayoutGrid,
  Inbox,
  ExternalLink,
  Mail,
  Store,
  Newspaper,
  CalendarDays,
  Users,
  FileText,
} from "lucide-react";
import { useAdminAuth } from "@/context/AdminAuthContext";

// Gegroepeerde zijbalk-navigatie (desktop). Volgorde = vaste structuur.
const NAV_GROUPS = [
  {
    label: "Overzicht",
    items: [{ to: "/beheer", label: "Dashboard", icon: LayoutGrid, exact: true }],
  },
  {
    label: "Inhoud",
    items: [
      { to: "/beheer/bedrijven", label: "Bedrijven", icon: Store },
      { to: "/beheer/agenda", label: "Agenda", icon: CalendarDays },
      { to: "/beheer/nieuws", label: "Nieuws", icon: Newspaper },
    ],
  },
  {
    label: "Inzendingen",
    items: [
      { to: "/beheer/inzendingen", label: "Inzendingen", icon: Inbox },
      { to: "/beheer/contact", label: "Contact", icon: Mail },
    ],
  },
  {
    label: "Documenten",
    items: [{ to: "/beheer/documenten", label: "Documenten", icon: FileText }],
  },
  {
    label: "Beheer",
    items: [{ to: "/beheer/gebruikers", label: "Gebruikers", icon: Users }],
  },
];

// Platte lijst voor de mobiele balk (volgorde gelijk aan groepen).
const NAV_FLAT = NAV_GROUPS.flatMap((g) => g.items);

const isActive = (pathname, n) =>
  n.exact ? pathname === n.to : pathname.startsWith(n.to);

export const AdminShell = ({ children }) => {
  const { admin, logout } = useAdminAuth();
  const location = useLocation();

  return (
    <div className="min-h-screen bg-[#F4F8FB]">
      <header
        data-testid="admin-header"
        className="sticky top-0 z-40 bg-white border-b border-[#E4EEF5]"
      >
        <div className="flex items-center h-16 px-4 sm:px-6 gap-6">
          <Link
            to="/beheer"
            className="flex items-baseline gap-1 font-[Poppins] leading-none shrink-0"
          >
            <span className="text-xl font-extrabold tracking-tight text-[#173A5E]">
              LEMMER
            </span>
            <span className="text-sm font-semibold text-[#2E9CD6] italic">
              Leeft
            </span>
            <span className="ml-2 px-2 py-0.5 rounded-md bg-[#173A5E] text-white text-[10px] font-[Poppins] font-bold uppercase tracking-wider">
              Beheer
            </span>
          </Link>

          <div className="flex items-center gap-3 ml-auto">
            <Link
              to="/"
              target="_blank"
              rel="noreferrer"
              data-testid="admin-view-site"
              className="hidden sm:inline-flex items-center gap-1.5 text-xs font-[Poppins] font-semibold text-[#2E9CD6] hover:text-[#173A5E] transition"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Bekijk site
            </Link>
            <div className="hidden sm:block text-right">
              <p className="text-xs text-[#5A7594] font-[Inter]">Ingelogd als</p>
              <p
                data-testid="admin-email"
                className="text-sm text-[#173A5E] font-[Poppins] font-semibold truncate max-w-[180px]"
              >
                {admin?.email}
              </p>
            </div>
            <button
              type="button"
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#F4F8FB] hover:bg-[#FDECEE] text-[#173A5E] hover:text-[#8b1420] font-[Poppins] font-semibold text-sm transition"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Uitloggen</span>
            </button>
          </div>
        </div>

        {/* Mobiele nav: horizontale balk (ongewijzigd principe) */}
        <nav className="md:hidden border-t border-[#E4EEF5] bg-white flex overflow-x-auto">
          {NAV_FLAT.map((n) => {
            const Icon = n.icon;
            const active = isActive(location.pathname, n);
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`admin-nav-${n.label.toLowerCase()}`}
                className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-[Poppins] font-medium whitespace-nowrap border-b-2 transition ${
                  active
                    ? "border-[#2E9CD6] text-[#2E9CD6]"
                    : "border-transparent text-[#173A5E]"
                }`}
              >
                <Icon className="w-4 h-4" />
                {n.label}
              </Link>
            );
          })}
        </nav>
      </header>

      <div className="flex">
        {/* Gegroepeerde zijbalk (desktop) */}
        <aside
          data-testid="admin-sidebar"
          className="hidden md:block w-60 shrink-0 bg-white border-r border-[#E4EEF5] min-h-[calc(100vh-4rem)] py-6 px-3 sticky top-16 self-start"
        >
          {NAV_GROUPS.map((group) => (
            <div key={group.label} className="mb-6">
              <p
                data-testid={`admin-navgroup-${group.label.toLowerCase()}`}
                className="px-3 mb-2 text-[11px] font-[Poppins] font-bold uppercase tracking-[0.14em] text-[#5A7594]"
              >
                {group.label}
              </p>
              <div className="space-y-1">
                {group.items.map((n) => {
                  const Icon = n.icon;
                  const active = isActive(location.pathname, n);
                  return (
                    <Link
                      key={n.to}
                      to={n.to}
                      data-testid={`admin-nav-${n.label.toLowerCase()}`}
                      className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-[Poppins] font-medium transition ${
                        active
                          ? "bg-[#EAF6FD] text-[#2E9CD6]"
                          : "text-[#173A5E] hover:bg-[#F4F8FB]"
                      }`}
                    >
                      <Icon className="w-4 h-4 shrink-0" />
                      {n.label}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </aside>

        <main className="flex-1 min-w-0 px-4 sm:px-6 lg:px-8 py-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminShell;
